import { ChevronRight, CircleParking, Mail, Phone } from 'lucide-react';
import type { OperationsApartmentStay } from '@/shared/api';
import { OtaLogo } from '@/features/properties/OtaLogo';
import { formatCalendarDate } from './operationsDate';
import { occupancyLabel, stayPhasePresentation } from './operationsPresentation';

export function OperationsStayFacts({
  stay,
  compact = false,
  checkInTime,
  checkOutTime,
}: {
  stay: OperationsApartmentStay;
  compact?: boolean;
  checkInTime?: string;
  checkOutTime?: string;
}) {
  const guest = stay.guestName ?? 'Gast nicht hinterlegt';
  const phase = stayPhasePresentation(stay.stayPhase);
  return (
    <section className="border-line/70 space-y-2 border-b pb-3 last:border-b-0 last:pb-0">
      <p className={`text-xs font-bold ${phaseText(stay.stayPhase)}`}>
        {phaseLine(stay, phase.label, checkInTime, checkOutTime)}
      </p>
      <div>
        <p className="text-ink truncate text-sm font-bold">{guest}</p>
        <p className="text-ink-soft mt-0.5 text-xs">{occupancyLabel(stay)}</p>
      </div>
      <Contact
        value={stay.phone}
        enabled={stay.canCall}
        href="tel:"
        label="Telefon"
        guest={guest}
        icon={Phone}
      />
      <Contact
        value={stay.email}
        enabled={stay.canEmail}
        href="mailto:"
        label="E-Mail"
        guest={guest}
        icon={Mail}
      />
      <div className="flex min-h-6 items-center gap-2 text-xs">
        {stay.channelCategory === 'Manual' ? (
          <span className="bg-ink-soft inline-flex size-5 items-center justify-center rounded-full text-[10px] font-bold text-white">
            M
          </span>
        ) : stay.channelCategory === 'OtherOta' ? (
          <span className="bg-ink-faint inline-flex size-5 items-center justify-center rounded-full text-[10px] font-bold text-white">
            ?
          </span>
        ) : (
          <OtaLogo channel={stay.channelCategory} size={20} />
        )}
        <span className="text-ink-soft">{stay.channelDisplayName}</span>
      </div>
      <div
        className={`flex items-center gap-2 text-xs font-semibold ${stay.parkingReserved ? 'text-sage' : 'text-ink-soft'}`}
      >
        <CircleParking size={17} strokeWidth={2.2} aria-hidden />
        {stay.parkingReserved
          ? stay.parkingSpotLabels.length
            ? `Parkplatz ${stay.parkingSpotLabels.join(', ')}`
            : 'Parkplatz reserviert'
          : 'Kein Parkplatz'}
      </div>
      {!compact && stay.reference && (
        <p className="text-ink-faint font-mono text-[10px]">{stay.reference}</p>
      )}
    </section>
  );
}

function phaseLine(
  stay: OperationsApartmentStay,
  label: string,
  checkInTime?: string,
  checkOutTime?: string,
) {
  if (stay.stayPhase === 'Departure')
    return `${label} · ${checkOutTime?.slice(0, 5) ?? formatCalendarDate(stay.checkOutDate)}`;
  if (stay.stayPhase === 'Arrival')
    return `${label} · ${checkInTime?.slice(0, 5) ?? formatCalendarDate(stay.checkInDate)}`;
  if (stay.stayPhase === 'InHouse')
    return `${label} · bis ${formatCalendarDate(stay.checkOutDate)}`;
  return `${label} · ${formatCalendarDate(stay.checkInDate)}`;
}

function Contact({
  value,
  enabled,
  href,
  label,
  guest,
  icon: Icon,
}: {
  value: string | null;
  enabled: boolean;
  href: string;
  label: string;
  guest: string;
  icon: typeof Phone;
}) {
  if (!enabled || !value) {
    return (
      <div className="text-ink-faint flex min-h-11 items-center gap-2 rounded-md px-2 text-xs">
        <Icon size={15} aria-hidden />
        {label} nicht hinterlegt
      </div>
    );
  }
  return (
    <a
      href={`${href}${value}`}
      aria-label={label === 'Telefon' ? `${guest} anrufen` : `E-Mail an ${guest}`}
      onClick={(event) => event.stopPropagation()}
      className="border-line text-ink hover:border-sage hover:text-sage flex min-h-11 items-center gap-2 rounded-md border px-2 text-xs transition-colors"
    >
      <Icon size={15} aria-hidden />
      <span className="min-w-0 flex-1 truncate">{value}</span>
      <ChevronRight size={14} aria-hidden />
    </a>
  );
}

function phaseText(phase: string) {
  if (phase === 'Departure') return 'text-sealwax';
  if (phase === 'Arrival') return 'text-amber';
  return 'text-sage';
}
